import { useEffect, useState } from "react";
import { api } from "../api.ts";
import { AvisoError, useAccion } from "./comun.tsx";

interface Estado { configurado: boolean; storeId: string; renovacion: boolean; tarjetasGuardadas: number }
interface Cobro { id: string; clinica: string; plan: string; periodo: string; total: number; estado: "pendiente" | "aprobado" | "cancelado" | "fallido" | "incierto"; creado: string; para_vence: string | null; detalle: string; tarjeta: string; payphone_id: number | null }

const dinero = (c: number) => `$ ${(c / 100).toFixed(2).replace(".", ",")}`;
const fecha = (iso: string) => new Date(iso).toLocaleString("es-EC", { dateStyle: "medium", timeStyle: "short" });
const ESTADO: Record<Cobro["estado"], [string, string]> = { aprobado: ["Cobrado", "azul"], fallido: ["No aprobado", ""], pendiente: ["En curso", ""], cancelado: ["Cancelado", ""], incierto: ["Revisar en Payphone", "rojo"] };

/**
 * Panel de Donna → «Cobro con tarjeta»: el dueño pone el Token y el StoreID de su aplicación de Payphone Developers (tipo Web).
 * El token se guarda cifrado y nunca se vuelve a mostrar. Con la «clave de codificación» (cuando Payphone autoriza la tokenización) se enciende la renovación automática.
 */
export function ConfigPayphone() {
  const [e, setE] = useState<Estado | null>(null);
  const [token, setToken] = useState("");
  const [storeId, setStoreId] = useState("");
  const [clave, setClave] = useState("");
  const [msg, setMsg] = useState("");
  const a = useAccion();
  const cargar = () => api<Estado>("GET", "/api/plataforma/payphone").then((x) => { setE(x); setStoreId(x.storeId); }).catch(a.setError);
  useEffect(() => { cargar(); }, []);

  async function guardar() {
    const r = await a.correr(() => api("PUT", "/api/plataforma/payphone", { token: token.trim(), storeId: storeId.trim() }));
    if (r) { setToken(""); setMsg("Guardado. Las clínicas ya pueden pagar su plan con tarjeta desde Plan y suscripción."); cargar(); }
  }
  async function quitar() {
    if (!confirm("¿Quitar las credenciales de Payphone? Se apaga el pago con tarjeta hasta que las pongas otra vez.")) return;
    const r = await a.correr(() => api("DELETE", "/api/plataforma/payphone"));
    if (r) { setMsg("Credenciales quitadas."); setToken(""); setStoreId(""); cargar(); }
  }
  async function guardarClave() {
    const r = await a.correr(() => api("PUT", "/api/plataforma/payphone/clave", { clave: clave.trim() }));
    if (r) { setClave(""); setMsg("Clave guardada. Las clínicas ya ven la opción «Renovar automáticamente» al pagar con tarjeta."); cargar(); }
  }
  async function quitarClave() {
    if (!confirm("¿Apagar la renovación automática? Las clínicas dejan de ver la opción y Donna deja de cobrar solo. Las tarjetas guardadas se conservan cifradas.")) return;
    const r = await a.correr(() => api("DELETE", "/api/plataforma/payphone/clave"));
    if (r) { setMsg("Renovación automática apagada."); cargar(); }
  }

  return (
    <div className="card" style={{ boxShadow: "none", display: "grid", gap: 8 }}>
      <h3 style={{ margin: 0 }}>Cobro con tarjeta (Payphone)</h3>
      <p style={{ margin: 0 }}>
        Las clínicas pagan su plan con tarjeta dentro de Donna, con la «Cajita de Pagos» de Payphone. Cuando Payphone confirma el pago, Donna lo registra solo (con el IVA incluido, método «Tarjeta») y extiende el plan.
        La factura la emites tú desde el historial de pagos, como siempre.
      </p>
      <div className={`aviso ${e?.configurado ? "ok" : "info"}`} role="status">{e === null ? "Revisando…" : e.configurado ? "El pago con tarjeta está activo." : "Todavía no está activo: faltan el Token y el StoreID."}</div>
      <ol style={{ margin: "0 0 0 18px", padding: 0, display: "grid", gap: 4 }}>
        <li>En <b>Payphone Developers</b> crea una aplicación de tipo <b>Web</b> con dominio <span className="mono">https://app.donnadent.com</span> y URL de respuesta <span className="mono">https://app.donnadent.com/pago/respuesta</span>.</li>
        <li>Copia el <b>Token</b> y el <b>StoreID</b> y pégalos aquí. No los compartas por chat ni por correo.</li>
        <li>Prueba primero con un pago pequeño (tu propia clínica) antes de avisar a tus clientes.</li>
      </ol>
      <label className="campo">Token de Payphone
        <input type="password" autoComplete="off" spellCheck={false} value={token} onChange={(x) => setToken(x.target.value)} placeholder={e?.configurado ? "•••••••• (ya hay uno guardado; pega otro para reemplazarlo)" : "Pega aquí el token"} />
      </label>
      <label className="campo">StoreID
        <input className="mono" autoComplete="off" spellCheck={false} value={storeId} onChange={(x) => setStoreId(x.target.value)} placeholder="Identificador de la tienda" />
      </label>
      <AvisoError error={a.error} />
      {msg && <div className="aviso ok" role="status">{msg}</div>}
      <div className="fila" style={{ flexWrap: "wrap" }}>
        <button className="btn pri" onClick={guardar} disabled={a.ocupado || token.trim().length < 20 || storeId.trim().length < 6}>Guardar</button>
        {e?.configurado && <button className="btn peligro" onClick={quitar} disabled={a.ocupado}>Quitar credenciales</button>}
      </div>
      <small className="muted">Payphone cobra una comisión por pago con tarjeta (según tu contrato) y no permite reversos después del día del cobro: los reembolsos de la garantía de 7 días se hacen a mano desde Payphone Business.</small>

      {e?.configurado && (
        <>
          <hr style={{ width: "100%", border: 0, borderTop: "1px solid var(--borde, #CBD8D4)" }} />
          <h3 style={{ margin: 0 }}>Renovación automática</h3>
          <p style={{ margin: 0 }}>
            Donna cobra sola cada mes (o año) a las clínicas que lo autorizaron, con la ficha de su tarjeta. Les avisa por correo 5 días antes, cobra el día del vencimiento y, si el banco no aprueba, reintenta 1, 3 y 5 días después.
          </p>
          <div className={`aviso ${e.renovacion ? "ok" : "info"}`} role="status">
            {e.renovacion ? `Renovación automática encendida · ${e.tarjetasGuardadas} tarjeta${e.tarjetasGuardadas === 1 ? "" : "s"} guardada${e.tarjetasGuardadas === 1 ? "" : "s"}.` : "Apagada: falta la clave de codificación (Payphone la entrega cuando autoriza la tokenización)."}
          </div>
          <ol style={{ margin: "0 0 0 18px", padding: 0, display: "grid", gap: 4 }}>
            <li>Pídele a <b>Payphone</b> que autorice la <b>tokenización</b> de tu negocio (tarda de 3 a 5 días hábiles; solo Visa y Mastercard).</li>
            <li>Cuando te aprueben, en <b>Payphone Developers</b> copia la <b>clave de codificación</b> (32 caracteres) y pégala aquí. No la compartas por chat ni por correo.</li>
            <li>Haz una prueba con tu propia clínica: paga con tarjeta marcando «Renovar automáticamente» y comprueba que aparece la tarjeta guardada.</li>
          </ol>
          <label className="campo">Clave de codificación de Payphone
            <input type="password" autoComplete="off" spellCheck={false} maxLength={64} value={clave} onChange={(x) => setClave(x.target.value)} placeholder={e.renovacion ? "•••••••• (ya hay una guardada; pega otra para reemplazarla)" : "32 caracteres"} />
          </label>
          <div className="fila" style={{ flexWrap: "wrap" }}>
            <button className="btn pri" onClick={guardarClave} disabled={a.ocupado || clave.trim().length !== 32}>Guardar clave</button>
            {e.renovacion && <button className="btn peligro" onClick={quitarClave} disabled={a.ocupado}>Apagar renovación automática</button>}
          </div>
          {e.renovacion && <CobrosAutomaticos alCambiar={cargar} />}
        </>
      )}
    </div>
  );
}

/** Los cobros automáticos de los últimos 60 días, con «Cobrar ahora» y la revisión de los que quedaron sin respuesta de Payphone. */
function CobrosAutomaticos({ alCambiar }: { alCambiar: () => void }) {
  const [lista, setLista] = useState<Cobro[] | null>(null);
  const [nums, setNums] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState("");
  const a = useAccion();
  const cargar = () => api<{ registros: Cobro[] }>("GET", "/api/plataforma/renovaciones").then((x) => setLista(x.registros)).catch(a.setError);
  useEffect(() => { cargar(); }, []);

  async function ahora() {
    const r = await a.correr(() => api<{ cobradas: number; fallidas: number; inciertas: number; avisos: number }>("POST", "/api/plataforma/renovaciones/procesar"));
    if (r) { setMsg(`Listo: ${r.avisos} aviso(s), ${r.cobradas} cobrada(s), ${r.fallidas} no aprobada(s), ${r.inciertas} para revisar.`); cargar(); alCambiar(); }
  }
  async function resolver(c: Cobro, resultado: "cobrado" | "no_cobrado") {
    if (!confirm(resultado === "cobrado" ? `¿Confirmas que Payphone SÍ cobró ${dinero(c.total)} a ${c.clinica}? Se registrará el pago y se extenderá su plan.` : `¿Confirmas que Payphone NO cobró este valor a ${c.clinica}?`)) return;
    const r = await a.correr(() => api("POST", `/api/plataforma/renovaciones/${c.id}/resolver`, { resultado, transaccion: Number(nums[c.id]) || undefined }));
    if (r) { setMsg("Cobro resuelto."); cargar(); }
  }

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div className="fila" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}>Cobros automáticos (últimos 60 días)</h3>
        <button className="btn chico" onClick={ahora} disabled={a.ocupado} title="Hace lo mismo que Donna hace sola cada 3 horas: avisa y cobra lo que toque hoy">Cobrar lo de hoy ahora</button>
      </div>
      <AvisoError error={a.error} />
      {msg && <div className="aviso ok" role="status">{msg}</div>}
      {lista && !lista.length && <p className="muted" style={{ margin: 0 }}>Todavía no hay cobros automáticos.</p>}
      <ul className="lista-abonos">
        {lista?.map((c) => (
          <li key={c.id} className="cumple">
            <div className="fila" style={{ justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap" }}>
              <span><b>{c.clinica}</b> · plan {c.plan} ({c.periodo}) · <b className="mono">{dinero(c.total)}</b></span>
              <span className={`chip ${ESTADO[c.estado][1]}`}>{ESTADO[c.estado][0]}</span>
            </div>
            <span className="muted" style={{ fontSize: 13 }}>{fecha(c.creado)}{c.para_vence ? ` · renueva el vencimiento del ${c.para_vence}` : ""}{c.tarjeta ? ` · ${c.tarjeta}` : ""}{c.detalle ? ` · ${c.detalle}` : ""}</span>
            {c.estado === "incierto" && (
              <div className="fila" style={{ flexWrap: "wrap", alignItems: "end" }}>
                <span style={{ fontSize: 13, flexBasis: "100%" }}>Payphone no respondió con claridad. <b>No se reintenta solo.</b> Entra a Payphone Business y busca el cobro con identificador <span className="mono">{c.id}</span>.</span>
                <label className="campo">Número de transacción (si se cobró)<input className="mono" inputMode="numeric" value={nums[c.id] ?? ""} onChange={(x) => setNums({ ...nums, [c.id]: x.target.value })} aria-label="Número de transacción de Payphone" /></label>
                <button className="btn chico pri" disabled={a.ocupado || !Number(nums[c.id])} onClick={() => resolver(c, "cobrado")}>Sí se cobró</button>
                <button className="btn chico" disabled={a.ocupado} onClick={() => resolver(c, "no_cobrado")}>No se cobró</button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
